import {
  CheckCircleFilled,
  CloseCircleFilled,
  LoadingOutlined,
} from '@ant-design/icons';
import { Alert, Timeline } from 'antd';
import { useEffect, useState } from 'react';
import { TaskStatus } from '../../task/components/task-status';
import type { ScriptFissionTaskDto } from '../../task/model';

function getInitialStep(status?: string): number {
  if (TaskStatus.isSettled(status)) return 5;
  if (status === 'running') return 3;
  return 1;
}

const base_steps = [
  { key: 1, label: '任务创建成功' },
  { key: 2, label: '产品分析' },
  { key: 3, label: '图片分析' },
  { key: 4, label: '脚本裂变' },
];

export const FissionStatus = (props: { task?: ScriptFissionTaskDto }) => {
  const { task } = props;

  const [step, setStep] = useState<number>(() => getInitialStep(task?.status));

  // 定时过渡：cleanup 确保外部状态提前到来时取消倒计时
  useEffect(() => {
    if (step === 1) {
      const timer = window.setTimeout(() => setStep(2), 2000);
      return () => window.clearTimeout(timer);
    }
    if (step === 3) {
      const timer = window.setTimeout(() => setStep(4), 3000);
      return () => window.clearTimeout(timer);
    }
  }, [step]);

  // 响应外部任务状态变化，触发 step 变化后会触发上方 effect cleanup 取消倒计时
  useEffect(() => {
    const status = task?.status;
    if (!status) return;

    if (TaskStatus.isSettled(status)) {
      setStep(5);
    } else if (status === 'running') {
      setStep((prev) => Math.max(prev, 3));
    }
  }, [task?.status]);

  if (!task) {
    return <p className="text-lg text-primary-500">请生成产品场景数据...</p>;
  }

  const is_failed = TaskStatus.isFailed(task.status);
  const is_settled = TaskStatus.isSettled(task.status);

  const getItemDot = (key: number) => {
    if (key < step) {
      return <CheckCircleFilled style={{ color: '#52c41a' }} />;
    }
    if (key === step) {
      if (is_failed) return <CloseCircleFilled style={{ color: '#ff4d4f' }} />;
      if (is_settled) return <CheckCircleFilled style={{ color: '#52c41a' }} />;
      return <LoadingOutlined style={{ color: '#1677ff' }} />;
    }
    return undefined;
  };

  const getItemColor = (key: number): string => {
    if (key < step) return 'green';
    if (key === step) {
      if (is_failed) return 'red';
      return is_settled ? 'green' : 'blue';
    }
    return 'gray';
  };

  const all_steps = [
    ...base_steps,
    { key: 5, label: is_failed ? '脚本裂变失败' : '脚本裂变完成' },
  ];

  return (
    <div className="text-left">
      <Timeline
        items={all_steps
          .filter((s) => s.key <= step)
          .map((s) => ({
            key: s.key,
            dot: getItemDot(s.key),
            color: getItemColor(s.key),
            content: s.label,
          }))}
      />
      {is_failed && task.error && (
        <Alert type="error" message={task.error} showIcon />
      )}
    </div>
  );
};
